<template>
  <!-- [전송 전환] — TLS 5061(기본) ↔ UDP 5060(임시 롤백). 확인창을 거쳐 PATCH transport -->
  <a-modal :open="open" :footer="null" :mask-closable="false" destroy-on-close @cancel="close">
    <template #title>
      <div class="modal-title">전송 전환</div>
    </template>

    <div class="modal-content">
      <div v-if="device" class="pbx-dev-tr">
        <p v-if="formError" class="pbx-dev-tr__error" role="alert">{{ formError }}</p>

        <p class="pbx-dev-tr__current">
          현재 <strong>{{ currentText }}</strong>
          <span v-if="device.sip_transport === null"> · 설치 장소 기본값을 따르는 중</span>
        </p>

        <a-radio-group v-model:value="transport" class="pbx-dev-tr__radios">
          <a-radio value="tls">TLS 5061(기본)</a-radio>
          <a-radio value="udp">UDP 5060(임시 롤백)</a-radio>
        </a-radio-group>

        <a-checkbox v-if="transport === 'tls'" v-model:checked="srtp">SRTP 필수</a-checkbox>
        <p v-else class="pbx-dev-tr__warn">
          UDP 는 통화 키가 평문으로 오갑니다 — 임시 롤백 용도로만 쓰세요.
        </p>
      </div>

      <div class="modal-footer pbx-dev-tr__footer">
        <a-button @click="close">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="saving" :disabled="!canSubmit" @click="submit">
          전환
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import { usePbxAction } from '@/views/Callbot/service/usePbxAction';

import { confirmPbx, deviceName, effectiveTransport, transportText } from './PbxDeviceShared';

const props = defineProps({
  open: { type: Boolean, default: false },
  inventory: { type: Object, required: true },
  /** PbxDeviceItem | null */
  device: { type: Object, default: null },
  /** PbxSiteItem | null */
  site: { type: Object, default: null },
});
const emit = defineEmits(['update:open']);

const { run, isPending } = usePbxAction(props.inventory);

/** 'tls' | 'udp' */
const transport = ref('tls');
const srtp = ref(false);
const formError = ref('');

const current = computed(() => (props.device ? effectiveTransport(props.device, props.site) : null));
const currentText = computed(() => (current.value ? transportText(current.value.transport, current.value.port) : ''));
// 포트는 보내지 않는다 — 서버가 TLS→5061 / UDP→5060 으로 채운다
const targetText = computed(() => transportText(transport.value));

const saving = computed(() => !!props.device && isPending(`device:transport:${props.device.id}`));
// 같은 전송·같은 SRTP 로는 보내지 않는다(UDP 는 SRTP 를 무시하므로 전송만 비교)
const changed = computed(() => {
  const c = current.value;
  if (!c) return false;
  if (transport.value !== c.transport) return true;
  return transport.value === 'tls' && srtp.value !== c.srtp;
});
const canSubmit = computed(() => changed.value && !saving.value);

watch(
  () => props.open,
  (open) => {
    if (!open || !current.value) return;
    transport.value = current.value.transport;
    srtp.value = current.value.srtp;
    formError.value = '';
  }
);

function close() {
  emit('update:open', false);
}

async function submit() {
  const device = props.device;
  if (!device || !canSubmit.value || !current.value) return;
  const target = transport.value;
  // 확인 문구 — UDP 경고 문장은 UDP 로 갈 때만 붙인다(TLS 로 되돌리는 확인에 평문 경고는 맞지 않다)
  const udpWarn = target === 'udp' ? ' UDP 는 통화 키가 평문으로 오갑니다 — 임시 롤백 용도로만 쓰세요.' : '';
  const ok = await confirmPbx({
    title: '전송 전환',
    content: `전화기 ${deviceName(device)} 의 SIP 전송을 ${currentText.value} → ${targetText.value} 으로 바꿉니다. 다음 설정 수신(또는 설정 재수신 성공) 후 적용됩니다.${udpWarn} 계속할까요?`,
    okText: '전환',
    danger: target === 'udp',
  });
  if (!ok) return;
  formError.value = '';
  const res = await run(
    `device:transport:${device.id}`,
    () =>
      props.inventory.api.updatePbxDeviceTransport(device.id, {
        sip_transport: target,
        ...(target === 'tls' ? { srtp_require: srtp.value } : {}),
      }),
    {
      success: `전송을 ${transportText(target)} 으로 바꿨습니다.`,
      reload: ['devices', 'extensions'],
      fallback: '전송을 바꾸지 못했습니다.',
      onError: (_err, text) => {
        formError.value = text;
        return true;
      },
    }
  );
  if (res) close();
}
</script>

<style scoped lang="scss">
// admin 모달 규약 — 전역 .modal-content(common.scss) 의 테두리·최소 높이는 antd 모달 안에서 쓰지 않는다
.modal-content {
  border: none;
  border-radius: 0;
  min-height: 0;
  display: block;
}

.pbx-dev-tr {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
  font-size: 13px;

  &__error {
    margin: 0;
    padding: 8px 12px;
    border: 1px solid #ffccc7;
    border-radius: 8px;
    background: #fff2f0;
    color: #cf1322;
    line-height: 1.5;
  }

  &__current {
    margin: 0;
    color: #595959;

    strong {
      color: #262626;
    }
  }

  &__radios {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__warn {
    margin: 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: #fffbe6;
    color: #d48806;
    font-size: 12px;
    line-height: 1.5;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
  }
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
